/**
 * Sources List Component
 * Displays sources returned from function calls (e.g., web search results)
 * This is an unpublished work containing 3M confidential and proprietary information.
 * Disclosure, use or reproduction without the written authorization of 3M is prohibited.
 * If publication occurs, the following notice applies:
 * Copyright (C) 2025, 3M. All rights reserved.
 */

import React, { useState } from 'react';
import { Link2Icon, ChevronDownIcon, ChevronRightIcon } from '@radix-ui/react-icons';
import { useTheme } from '../../Theme/ThemeProvider';

interface Source {
  title: string;
  url: string;
  snippet?: string;
}

interface SourcesListProps {
  sources: Source[];
  hideIcons?: boolean; // Hide favicon circles for RAG sources (DevOps, Legal, etc.)
  hideUrlDetails?: boolean; // Hide URL hostname/domain for RAG sources (show only title)
}

const getFaviconUrl = (url: string): string => {
  try {
    const domain = new URL(url).hostname;
    return `https://www.google.com/s2/favicons?domain=${domain}&sz=64`;
  } catch {
    return '';
  }
};

export const SourcesList: React.FC<SourcesListProps> = ({ sources, hideIcons = false, hideUrlDetails = false }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { mode } = useTheme();

  if (!sources || sources.length === 0) {
    return null;
  }

  // Remove duplicate sources based on URL
  const uniqueSources = sources.reduce((acc: Source[], current) => {
    const isDuplicate = acc.some(item => item.url === current.url);
    if (!isDuplicate) {
      acc.push(current);
    }
    return acc;
  }, []);

  return (
    <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-2 w-full text-left hover:opacity-80 transition-opacity"
      >
        <div className="flex items-center gap-2">
          <div className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--color-text)' }}>
            Sources
          </div>
          
          {/* Overlapping favicon circles - only show for voice mode web search */}
          {!hideIcons && (
            <div className="flex items-center -space-x-2">
            {uniqueSources.slice(0, 3).map((source, index) => {
              const faviconUrl = getFaviconUrl(source.url);
              return (
                <div
                  key={index}
                  className="w-5 h-5 rounded-full bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 flex items-center justify-center overflow-hidden"
                  style={{ zIndex: uniqueSources.length - index }}
                >
                  {faviconUrl ? (
                    <img
                      src={faviconUrl}
                      alt=""
                      className="w-3 h-3 object-cover"
                      onError={(e) => {
                        // Fallback to link icon on error
                        e.currentTarget.style.display = 'none';
                        e.currentTarget.nextElementSibling?.classList.remove('hidden');
                      }}
                    />
                  ) : null}
                  <Link2Icon className="w-3 h-3 text-gray-400 hidden" />
                </div>
              );
            })}
            {uniqueSources.length > 3 && (
              <div className="w-5 h-5 rounded-full bg-gray-100 dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-700 flex items-center justify-center text-xs text-gray-500 dark:text-gray-400 font-medium">
                +{uniqueSources.length - 3}
              </div>
            )}
          </div>
          )}

          {/* Caret icon */}
          {isExpanded ? (
            <ChevronDownIcon className="w-4 h-4" style={{ color: 'var(--color-text)' }} />
          ) : (
            <ChevronRightIcon className="w-4 h-4" style={{ color: 'var(--color-text)' }} />
          )}
        </div>
      </button>

      {/* Collapsible content */}
      {isExpanded && (
        <div className="space-y-2 mt-3">
          {uniqueSources.map((source, index) => (
            <a
              key={index}
              href={source.url}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-start gap-2 p-2 rounded-md transition-colors"
              onMouseEnter={e => (e.currentTarget.style.backgroundColor = mode === 'dark' ? '#1f2937' : '#eff6ff')}
              onMouseLeave={e => (e.currentTarget.style.backgroundColor = '')}
            >
              <Link2Icon className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: mode === 'dark' ? '#60a5fa' : '#1d4ed8' }} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium group-hover:underline truncate" style={{ color: mode === 'dark' ? '#60a5fa' : '#1d4ed8' }}>
                  {source.title}
                </div>
                {source.snippet && !hideUrlDetails && (
                  <div className="text-xs line-clamp-2 mt-1" style={{ color: mode === 'dark' ? '#9ca3af' : '#4b5563' }}>
                    {source.snippet}
                  </div>
                )}
                {!hideUrlDetails && (
                  <div className="text-xs truncate mt-0.5" style={{ color: mode === 'dark' ? '#6b7280' : '#9ca3af' }}>
                    {source.url.replace(/^https?:\/\//i, '')}
                  </div>
                )}
              </div>
            </a>
          ))}
        </div>
      )}
    </div>
  );
};

export default SourcesList;
